import {Flag, Separator} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {find, isNotNil, propEq, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {FinancingEndingPreflightRowResponseBody} from '@dms/api/sales';
import {testIds} from '@dms/routes';

import type {Nullish} from 'shared/web';

import {getFinancingEndingReasonLabel} from '../../utils/getFinancingEndingReasonLabel';

export type FinancingEndingRowLabels = {
  rowId: string;
  customerName: string | Nullish;
  vehicleTitle: string | Nullish;
  registrationPlate: string | Nullish;
  endOfFinancing: string | Nullish;
};

export type FinancingEndingSkipSummaryProps = {
  skippedRows: FinancingEndingPreflightRowResponseBody[];
  rowLabels: FinancingEndingRowLabels[];
};

const VEHICLE_LABEL_SEPARATOR = ' · ';

const getVehicleLabel = (rowLabels: FinancingEndingRowLabels | Nullish) =>
  [rowLabels?.vehicleTitle, rowLabels?.registrationPlate]
    .filter(isNotNilOrEmpty)
    .join(VEHICLE_LABEL_SEPARATOR);

export function FinancingEndingSkipSummary(props: FinancingEndingSkipSummaryProps) {
  return (
    <VStack spacing={3}>
      {props.skippedRows.map((row) => {
        const rowLabels = find(propEq(row.rowId, 'rowId'), props.rowLabels);
        const vehicleLabel = getVehicleLabel(rowLabels);
        const [primaryCode, ...secondaryCodes] = row.reasonCodes;
        const secondaryLabels = uniq(secondaryCodes.map(getFinancingEndingReasonLabel));

        return (
          <VStack
            key={row.rowId}
            spacing={3}
            data-testid={testIds.businessCase.financingEnding('skippedRow')}
          >
            <Separator spacing={0} />
            <HStack spacing={3} justify="space-between" align="flex-start">
              <VStack spacing={1}>
                <Show
                  when={isNotNilOrEmpty(rowLabels?.customerName) ? rowLabels?.customerName : null}
                >
                  {(customerName) => <Text size="small">{customerName}</Text>}
                </Show>
                <Show when={isNotNilOrEmpty(vehicleLabel)}>
                  <Text size="small" color="tertiary">
                    {vehicleLabel}
                  </Text>
                </Show>
              </VStack>
              <VStack spacing={1} align="flex-end">
                <Flag
                  colorScheme="orange"
                  isSubtle
                  size="small"
                  label={getFinancingEndingReasonLabel(primaryCode)}
                  data-testid={testIds.businessCase.financingEnding('skipReason-primary')}
                />
                <Show when={isNotNil(secondaryLabels[0])}>
                  <Text
                    size="xSmall"
                    color="tertiary"
                    align="right"
                    data-testid={testIds.businessCase.financingEnding('skipReason-secondary')}
                  >
                    {secondaryLabels.join(', ')}
                  </Text>
                </Show>
              </VStack>
            </HStack>
          </VStack>
        );
      })}
    </VStack>
  );
}
